import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Dropdown.css";
import { FaChevronDown } from "react-icons/fa";
import { includesSearchText, normalizeSearchText } from "../../utils/searchText";

const Dropdown = ({
  options = [],
  label,
  placeholder,
  onChange,
  onBlur,
  onFocus,
  required = false,
  value,
  style,
  disabled,
  className = "",
}) => {
  const MENU_GAP = 6;
  const MENU_Z_INDEX = 3000;
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPlacement, setMenuPlacement] = useState("bottom");
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const menuRef = useRef(null);
  const optionsRef = useRef(null);
  const hasValue = String(value ?? "").trim().length > 0;
  const labelText = useMemo(() => String(label ?? placeholder ?? "").trim(), [label, placeholder]);
  const fieldPlaceholder = useMemo(() => String(placeholder ?? "").trim(), [placeholder]);
  const showFloatingLabel = labelText.length > 0;
  const enableSearch = options.length > 5;
  const hideClosedPlaceholder = showFloatingLabel && !hasValue && !(isOpen || isFocused);

  const selectedOption = useMemo(() => {
    const currentValue = String(value ?? "");
    return options.find((opt) => String(opt.value) === currentValue) || null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!enableSearch) return options;
    const q = normalizeSearchText(search);
    if (!q) return options;
    return options.filter((opt) => includesSearchText(opt.label, q));
  }, [enableSearch, options, search]);

  const emitChange = (nextValue) => {
    if (typeof onChange !== "function") return;
    onChange({ target: { value: nextValue } });
  };

  const emitBlur = useCallback(
    (nextValue = value ?? "") => {
      if (typeof onBlur !== "function") return;
      onBlur({ target: { value: nextValue } });
    },
    [onBlur, value]
  );

  const computeMenuPosition = useCallback(() => {
    if (typeof window === "undefined") return null;
    const containerEl = containerRef.current;
    if (!containerEl) return null;

    const rect = containerEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;

    const defaultMaxHeight = enableSearch ? 300 : 240;
    const searchHeight = enableSearch
      ? searchContainerRef.current?.getBoundingClientRect().height ?? 60
      : 0;
    const estimatedOptionHeight = 36;
    const desiredOptionsHeight = optionsRef.current
      ? Math.min(240, optionsRef.current.scrollHeight)
      : Math.min(240, filteredOptions.length * estimatedOptionHeight);
    const desiredMenuHeight = Math.min(defaultMaxHeight, searchHeight + desiredOptionsHeight);

    let placement = "bottom";
    if (spaceBelow >= desiredMenuHeight) {
      placement = "bottom";
    } else if (spaceAbove >= desiredMenuHeight) {
      placement = "top";
    } else {
      placement = spaceAbove > spaceBelow ? "top" : "bottom";
    }

    const availableSpace = placement === "bottom" ? spaceBelow : spaceAbove;
    const maxHeight = Math.min(defaultMaxHeight, Math.max(0, availableSpace));

    const width = rect.width;
    const maxLeft = viewportWidth - MENU_GAP - width;
    const left = Math.max(MENU_GAP, Math.min(rect.left, maxLeft));

    const baseStyle = {
      position: "fixed",
      left,
      width,
      maxHeight,
      zIndex: MENU_Z_INDEX,
    };

    if (placement === "bottom") {
      return { placement, style: { ...baseStyle, top: rect.bottom + MENU_GAP } };
    }

    return { placement, style: { ...baseStyle, bottom: viewportHeight - rect.top + MENU_GAP } };
  }, [MENU_GAP, MENU_Z_INDEX, enableSearch, filteredOptions.length]);

  const updateMenuPosition = useCallback(() => {
    const nextPosition = computeMenuPosition();
    if (!nextPosition) return;
    setMenuPlacement((prev) => (prev === nextPosition.placement ? prev : nextPosition.placement));
    setMenuStyle((prev) => {
      if (!prev) return nextPosition.style;
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextPosition.style);
      if (prevKeys.length !== nextKeys.length) return nextPosition.style;
      for (const key of nextKeys) {
        if (prev[key] !== nextPosition.style[key]) return nextPosition.style;
      }
      return prev;
    });
  }, [computeMenuPosition]);

  const closeMenu = useCallback(
    (reasonValue) => {
      setIsOpen(false);
      setSearch("");
      setIsFocused(false);
      setMenuStyle(null);
      emitBlur(reasonValue);
    },
    [emitBlur]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e) => {
      const containerEl = containerRef.current;
      const menuEl = menuRef.current;
      if (!containerEl) return;
      if (containerEl.contains(e.target)) return;
      if (menuEl && menuEl.contains(e.target)) return;
      closeMenu();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (!isOpen || !menuStyle) return;
    if (enableSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [enableSearch, isOpen, menuStyle]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [isOpen, updateMenuPosition]);

  const menu = isOpen && !disabled && menuStyle && (
    <div
      ref={menuRef}
      className={`custom-select-menu ${menuPlacement === "top" ? "custom-select-menu-top" : ""}`}
      role="listbox"
      style={menuStyle}
    >
      {enableSearch && (
        <div ref={searchContainerRef} className="custom-select-search">
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara..."
            aria-label="dropdown-search"
          />
        </div>
      )}

      <div ref={optionsRef} className="custom-select-options">
        {filteredOptions.length === 0 ? (
          <div className="custom-select-empty">Sonuç bulunamadı</div>
        ) : (
          filteredOptions.map((opt, idx) => {
            const isSelected = String(opt.value) === String(value ?? "");
            const isOptionDisabled = Boolean(opt.disabled);
            return (
              <button
                key={`${String(opt.value)}-${idx}`}
                type="button"
                disabled={isOptionDisabled}
                title={isOptionDisabled ? opt.disabledReason : undefined}
                className={`custom-select-option ${isSelected ? "custom-select-option-selected" : ""} ${isOptionDisabled ? "custom-select-option-disabled" : ""}`}
                onClick={() => {
                  if (isOptionDisabled) return;
                  emitChange(opt.value);
                  closeMenu(opt.value);
                }}
              >
                {opt.label}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`custom-select-container ${className} ${disabled ? "custom-select-container-disabled" : ""}`}
      style={style}
    >
      {showFloatingLabel && (
        <label className={`dropdown-floating-label ${isOpen || isFocused || hasValue ? "floating" : ""}`}>
          {labelText}
          {required && <span className="dropdown-required">*</span>}
        </label>
      )}

      <button
        type="button"
        className="custom-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={labelText || "dropdown"}
        onClick={() => {
          if (disabled) return;
          if (isOpen) {
            closeMenu();
            return;
          }
          setIsFocused(true);
          onFocus?.();
          updateMenuPosition();
          setIsOpen(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          if (!isOpen) emitBlur();
        }}
      >
        {selectedOption ? (
          <span className="custom-select-value">{selectedOption.label}</span>
        ) : (
          <span className="custom-select-placeholder">
            {hideClosedPlaceholder ? "\u00A0" : showFloatingLabel ? "Seçiniz" : fieldPlaceholder || "Seçiniz"}
          </span>
        )}
        <FaChevronDown className={`dropdown-icon ${isOpen ? "dropdown-icon-open" : ""}`} />
      </button>

      {typeof document !== "undefined" ? createPortal(menu, document.body) : menu}
    </div>
  );
};

export default Dropdown;
